<script lang="ts" setup>
import SimpleBaseDialog from '@/components/dialogs/SimpleBaseDialog.vue'
import TextInput from '@/components/inputs/TextInput.vue'

const props = withDefaults(
  defineProps<{
    /** When provided the dialog is in edit mode: the domain is locked and only changed fields are emitted. */
    emailDomain?: BusinessEmailDomain | null
    defaultDisplayName: string
    loading?: boolean
  }>(),
  {
    emailDomain: null,
    loading: false,
  }
)

const emit = defineEmits<{
  action: [payload: Dialogs['business/SenderEmailConfigureDialog']['contextData']]
  close: []
}>()

const { t } = useI18n()
const businessStore = useBusinessStore()
const { track } = useAnalytics()

const DEFAULT_LOCAL_PART = 'no-reply'

const isEditMode = computed(() => props.emailDomain !== null)

const localPart = ref(props.emailDomain?.senderLocalPart ?? '')
const domain = ref(props.emailDomain?.domain ?? '')
const displayName = ref(props.emailDomain?.senderDisplayName ?? props.defaultDisplayName)

const showLocalPartTooltip = ref(false)
const shakeLocalPartInput = ref(false)
let localPartTooltipTimeout: ReturnType<typeof setTimeout> | undefined

const localPartInput = useTemplateRef('localPartInput')

const onLocalPartInput = (value: string) => {
  const sanitized = sanitizeSenderLocalPart(value)
  localPart.value = sanitized
  if (sanitized === value) return
  // Illegal character was typed - flash the tooltip and hide it after 2 seconds.
  showLocalPartTooltip.value = true
  clearTimeout(localPartTooltipTimeout)
  localPartTooltipTimeout = setTimeout(() => {
    showLocalPartTooltip.value = false
  }, 2000)
  shakeLocalPartInput.value = true
  // If an illegal character is typed in, Vuetify won't update the input UI element
  // with the sanitized string, so we have to do it manually
  const input = localPartInput.value?.$el.querySelector('input')
  if (input) input.value = sanitized
}

onBeforeUnmount(() => clearTimeout(localPartTooltipTimeout))

// In-field validation messages (passed to TextInput :rules).
const domainRule = (value: string) =>
  value.length === 0 || isSenderSubdomain(value) || t('business.emails.configure.domainError')
const displayNameRule = (value: string) =>
  value.length === 0 || isSenderDisplayName(value) || t('business.emails.configure.displayNameError')

// Drives the inline "@" color
const isDomainInvalid = computed(() => domain.value.length > 0 && !isSenderSubdomain(domain.value))
const isDisplayNameInvalid = computed(() => !isSenderDisplayName(displayName.value))

const preview = computed(() => {
  const local = localPart.value || DEFAULT_LOCAL_PART
  return `${displayName.value} <${local}@${domain.value || '…'}>`
})

const isValid = computed(() => isSenderSubdomain(domain.value) && isSenderDisplayName(displayName.value))

// Edit mode emits only the fields that actually changed (the domain is immutable).
const changedFields = computed<UpdateEmailDomainPayload>(() => {
  const updates: UpdateEmailDomainPayload = {}
  const nextLocalPart = localPart.value || DEFAULT_LOCAL_PART
  if (nextLocalPart !== props.emailDomain?.senderLocalPart) updates.senderLocalPart = nextLocalPart
  if (displayName.value !== props.emailDomain?.senderDisplayName) updates.senderDisplayName = displayName.value
  return updates
})

const canSubmit = computed(() => {
  if (!isValid.value) return false
  if (isEditMode.value) return Object.keys(changedFields.value).length > 0
  return true
})
const actionText = computed(() =>
  isEditMode.value ? t('global.save') : t('business.emails.actions.continueToVerification')
)
const dialogTitle = computed(() =>
  isEditMode.value ? t('business.emails.customAddress.editTitle') : t('business.emails.customAddress.title')
)

const onSubmit = () => {
  if (!canSubmit.value) return
  if (isEditMode.value) {
    emit('action', changedFields.value)
    return
  }
  emit('action', {
    domain: domain.value.toLowerCase(),
    senderLocalPart: localPart.value || DEFAULT_LOCAL_PART,
    senderDisplayName: displayName.value,
  })
}

const resetAnimation = () => {
  // we reset the animation state so that the input field could continously "shake" as users enter the invalid input
  shakeLocalPartInput.value = false
}

const onDismiss = () => {
  if (!isEditMode.value) {
    track(ANALYTICS_EVENTS.DOMAIN_CONFIGURE_DISMISSED, { businessId: businessStore.id })
  }
  emit('close')
}
</script>

<template>
  <simple-base-dialog
    :title="dialogTitle"
    :action-text="actionText"
    :cancel-text="t('global.cancel')"
    :disabled="!canSubmit"
    :loading="loading"
    max-width="600"
    divider
    refreshed
    data-cy="sender_email_configure_dialog"
    @action="onSubmit"
    @close="onDismiss"
  >
    <div v-if="!isEditMode" class="send-only-notice">
      <v-icon class="send-only-notice__icon" :size="16">custom:info</v-icon>
      <span>{{ t('business.emails.configure.warning') }}</span>
    </div>
    <div class="sender-row">
      <div
        class="sender-row__local"
        :class="{ 'sender-row__local--shake': shakeLocalPartInput }"
        @animationend.self="resetAnimation"
      >
        <text-input
          id="sender-local-part"
          ref="localPartInput"
          :model-value="localPart"
          :placeholder="DEFAULT_LOCAL_PART"
          hide-details
          autofocus
          data-cy="sender_local_part_input"
          @update:model-value="onLocalPartInput"
        />
        <v-tooltip
          :model-value="showLocalPartTooltip"
          :open-on-hover="false"
          :open-on-focus="false"
          activator="parent"
          location="top"
          content-class="bg-skribbleu"
          transition="slide-y-reverse-transition"
          :text="t('business.emails.configure.localPartTooltip')"
          data-cy="local_part_tooltip"
        />
      </div>
      <!-- Raw v-text-field here (not TextInput) because the inline "@" prefix needs the prepend-inner slot. -->
      <v-text-field
        id="sender-domain"
        :model-value="domain"
        placeholder="mail.company.com"
        :disabled="isEditMode"
        :rules="[domainRule]"
        variant="filled"
        clearable
        clear-icon="custom:clear_small"
        persistent-clear
        class="sender-row__domain"
        data-cy="sender_domain_input"
        @update:model-value="value => (domain = value ?? '')"
      >
        <template #prepend-inner>
          <span class="sender-row__at" :class="{ 'sender-row__at--error': isDomainInvalid }">@</span>
        </template>
      </v-text-field>
    </div>
    <v-divider class="mb-6 mt-4" />
    <p class="display-name-label">
      {{ t('business.emails.configure.displayNameLabel') }}
      <span
        v-if="!displayName.trim()"
        class="display-name-label__required"
        :class="{ 'display-name-label__required--error': isDisplayNameInvalid }"
        >- {{ t('global.required') }}</span
      >
    </p>
    <text-input
      id="sender-display-name"
      :model-value="displayName"
      :rules="[displayNameRule]"
      hide-details="auto"
      clearable
      clear-icon="custom:clear_small"
      persistent-clear
      data-cy="sender_display_name_input"
      @update:model-value="value => (displayName = value ?? '')"
    />
    <div class="preview-row">
      <span class="preview-row__label">{{ t('business.emails.configure.preview') }}</span>
      <span class="preview-row__pill" data-cy="preview_pill">{{ preview }}</span>
    </div>
  </simple-base-dialog>
</template>

<style lang="scss" scoped>
.send-only-notice {
  display: flex;
  align-items: flex-start;
  gap: 3 * $bw;
  margin-bottom: 2rem;
  padding: 4 * $bw;
  color: $c-to-sign-status;
  font-size: 1rem;
  line-height: 1.25rem;
  background-color: $c-to-sign-bg;
  border-radius: 6px;
  &__icon {
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.sender-row {
  display: flex;
  align-items: flex-start;
  gap: 3 * $bw;
  &__local {
    position: relative;
    flex: 0 0 120px;
    &--shake {
      animation: shake 0.3s ease;
    }
  }
  &__domain {
    flex: 1 1 auto;
  }
  &__at {
    align-self: center;
    color: $c-grey-dark;
    font-size: 1.125rem;
    &--error {
      color: $c-danger;
    }
  }
}

// The clear icon uses currentColor, so set its colour (#65728E) and 20x20 size here at the usage site.
:deep(.v-field__clearable) .v-icon {
  color: $c-grey-dark;
  font-size: 20px;
}

// Vuetify recolours X icon to red when input is invalid, this keeps X grey
:deep(.v-field--error:not(.v-field--disabled) .v-field__clearable > .v-icon) {
  color: $c-grey-dark;
}

.display-name-label {
  margin-bottom: 2 * $bw;
  color: $c-grey-darker;
  font-weight: 700;
  &__required {
    color: $c-grey-dark;
    font-weight: 400;
    text-transform: lowercase;
    font-size: 0.875rem;
    &--error {
      color: $c-danger;
    }
  }
}

.preview-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 3 * $bw;
  margin-top: 4 * $bw;
  &__label {
    color: $c-grey-dark;
    font-size: 0.875rem;
    line-height: 1.625rem;
  }
  &__pill {
    padding: 2px 13px;
    color: $c-grey-darker;
    font-size: 0.875rem;
    line-height: 1.625rem;
    word-break: break-all;
    background-color: $c-waiting-filter;
    border-radius: 4px;
  }
}
</style>
